import React, { useEffect } from 'react';
import TransMessagesTable from './TransMessagesTable';
import TransMessagesFilters from './TransMessagesFilters';
import TransMessageModal from './TransMessageModal';
import ConfirmDialog from './ConfirmDialog';
import NotificationProvider from './NotificationProvider';
import { useTransMessages } from './hooks/useTransMessages';
import { Alert } from '@material-tailwind/react';
import { getPostingStatus } from './utils/postingStatus';

const TransMessagesContainer = () => {
    const {
        messages,
        loading,
        error,
        pagination,
        filters,
        sortBy,
        sortOrder,
        selectedMessage,
        showModal,
        deleteDialogOpen,
        retryDialogOpen,
        snackbar,
        fetchMessages,
        handleRetryPosting,
        handleDeleteMessage,
        handleMarkAsUsed,
        handleRequeueMessage,
        handleBulkRequeue,
        setFilters,
        handleSortChange,
        setShowModal,
        setDeleteDialogOpen,
        setRetryDialogOpen,
        setSelectedMessage,
        goToPage,
        hideSnackbar
    } = useTransMessages();

    // Only fetch once on mount
    useEffect(() => {
        fetchMessages();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Intentionally only run once on mount

    if (loading && messages.length === 0) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-500"></div>
            </div>
        );
    }

    return (
        <NotificationProvider snackbar={snackbar} onClose={hideSnackbar}>
            <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA]">
                {/* Header Section */}
                <div className="border-b border-[#e8ddd1] bg-[#fff8f5] px-6 pb-4 pt-6">
                    {/* Header */}
                    <div className="text-center mb-6">
                        <h1 className="text-4xl font-bold text-[#2a2e30] mb-2">Trans Messages Management</h1>
                        <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full"></div>
                    </div>

                    {/* Filters */}
                    <TransMessagesFilters
                        filters={filters}
                        onFiltersChange={setFilters}
                        onRefresh={() => fetchMessages(pagination.currentPage, filters)}
                        loading={loading}
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onSortChange={handleSortChange}
                        totalMessages={pagination.totalMessages}
                        visibleCount={messages.length}
                    />

                    {/* Error Display */}
                    {error && (
                        <Alert color="red" className="mb-4">
                            {error}
                        </Alert>
                    )}
                </div>

                {/* Table Section */}
                <div className="px-6 pb-6 pt-4">
                    <TransMessagesTable
                        messages={messages}
                        loading={loading}
                        pagination={pagination}
                        onPageChange={goToPage}
                        onViewMessage={(message) => {
                            setSelectedMessage(message);
                            setShowModal(true);
                        }}
                        onRetryMessage={(message) => {
                            setSelectedMessage(message);
                            setRetryDialogOpen(true);
                        }}
                        onDeleteMessage={(message) => {
                            setSelectedMessage(message);
                            setDeleteDialogOpen(true);
                        }}
                        onMarkAsUsed={handleMarkAsUsed}
                        onRequeueMessage={handleRequeueMessage}
                        onBulkRequeue={handleBulkRequeue}
                    />
                </div>

                {/* Message Details Modal */}
                <TransMessageModal
                    open={showModal}
                    message={selectedMessage}
                    onClose={() => setShowModal(false)}
                />

                {/* Delete Confirmation Dialog */}
                <ConfirmDialog
                    open={deleteDialogOpen}
                    title="Confirm Delete"
                    message="Are you sure you want to delete this trans message? This action cannot be undone."
                    confirmText="Delete"
                    confirmColor="red"
                    onConfirm={() => selectedMessage && handleDeleteMessage(selectedMessage.id)}
                    onCancel={() => setDeleteDialogOpen(false)}
                    disabled={getPostingStatus(selectedMessage) === 'posted'}
                    warningMessage={getPostingStatus(selectedMessage) === 'posted' ?
                        "Warning: This message has already been posted and cannot be deleted." : null}
                />

                {/* Retry Posting Dialog */}
                <ConfirmDialog
                    open={retryDialogOpen}
                    title="Retry Posting"
                    message="Are you sure you want to retry posting this failed message? It will be added back to the posting queue."
                    confirmText="Retry Posting"
                    confirmColor="blue"
                    onConfirm={() => selectedMessage && handleRetryPosting(selectedMessage.id)}
                    onCancel={() => setRetryDialogOpen(false)}
                />
            </div>
        </NotificationProvider>
    );
};

export default TransMessagesContainer;
